import React, { useEffect, useRef, useState } from 'react';
import { Flex } from '@/layout';
import { Button } from '@/components/UI/Button';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.9.179/pdf.worker.min.js';

const PDFBookReader: React.FC<{ pdfUrl: string; downloadLabel: string }> = ({ pdfUrl, downloadLabel }) => {
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const [pdf, setPdf] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rendering, setRendering] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let disposed = false;
    setPdf(null);
    setCurrentPage(1);
    setFailed(false);
    setRendering(true);
    const task = getDocument({
      url: pdfUrl, isEvalSupported: false,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.9.179/cmaps/', cMapPacked: true,
    });
    task.promise.then(document => { if (!disposed) setPdf(document); }).catch(() => {
      if (!disposed) { setFailed(true); setRendering(false); }
    });
    return () => {
      disposed = true;
      void task.destroy().catch(() => undefined);
    };
  }, [pdfUrl]);

  useEffect(() => {
    let disposed = false;
    let task: any;
    const container = viewerRef.current;
    if (container) container.replaceChildren();
    if (!pdf || !container) return;
    setRendering(true);
    const render = async () => {
      try {
        const page = await pdf.getPage(currentPage);
        if (disposed) return;
        const viewport = page.getViewport({ scale: 1 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.style.display = 'block';
        const canvasContext = canvas.getContext('2d');
        if (!canvasContext) throw new Error('PDF canvas context is unavailable.');
        task = page.render({ canvasContext, viewport });
        await task.promise;
        if (!disposed) container.replaceChildren(canvas);
      } catch {
        if (!disposed) setFailed(true);
      } finally {
        if (!disposed) setRendering(false);
      }
    };
    void render();
    return () => { disposed = true; task?.cancel(); };
  }, [pdf, currentPage]);

  return (
    <div className="pdf-modal" style={{ width: '100%', minWidth: 0 }}>
      {failed && <div role="alert"><a href={pdfUrl} target="_blank" rel="noreferrer">{downloadLabel}</a></div>}
      <div className="pdf-content" ref={viewerRef} aria-busy={rendering} style={{ width: '100%' }} />
      <Flex justifyContent="center" alignItems="center" gridGap="15px" padding="20px 0">
        <Button variant="primary" type="button" onClick={() => setCurrentPage(page => page - 1)} disabled={rendering || failed || currentPage <= 1}>{'<'}</Button>
        <span aria-live="polite">{currentPage} / {pdf?.numPages}</span>
        <Button variant="primary" type="button" onClick={() => setCurrentPage(page => page + 1)} disabled={rendering || failed || !pdf || currentPage >= pdf.numPages}>{'>'}</Button>
      </Flex>
    </div>
  );
};

export default PDFBookReader;
